import { Link, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

import PhoneInputLib from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import { PostService } from "../../../Services/ConstantService";
import { API_URL } from "../../../Services/ApiService";

const PhoneInput = PhoneInputLib?.default || PhoneInputLib;

export default function TrackOrder() {
  const navigate = useNavigate();

  /*
   * Yup Validation Schema
   */
  const validationSchema = useMemo(
    () =>
      Yup.object({
        trackingId: Yup.string()
          .trim()
          .required("Please enter your order or tracking number."),

        mobile: Yup.string()
          .required("Please enter your phone number.")
          .test(
            "valid-phone",
            "Please enter a valid phone number.",
            (value) => {
              if (!value) return false;

              const digits = value.replace(/\D/g, "");

              return digits.length >= 10;
            },
          ),
      }),
    [],
  );

  /*
   * Formik
   */
  const formik = useFormik({
    initialValues: {
      trackingId: "",
      mobile: "",
    },

    validationSchema,

    enableReinitialize: true,

    onSubmit: async (values, { setStatus }) => {
      try {
        setStatus(null);

        const payload = {
          order_number: values.trackingId.trim(),
          mobile_number: values.mobile.startsWith("+")
            ? values.mobile
            : `+${values.mobile}`,
        };

        const res = await PostService(API_URL.TRACK_ORDER, payload);

        navigate("/track-order", {
          state: res?.data,
        });
      } catch (err) {
        const errorMessage =
          err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong. Please check and try again.";

        setStatus(errorMessage);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    status,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
  } = formik;

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-9 col-lg-7 col-xl-6">
          {/* Main Card */}
          <div className="card border-0 shadow-lg rounded-4 overflow-hidden">
            {/* Header */}
            <div className="bgLightSecondary px-4 py-3 text-center">
              <div
                className="bg-white text-primary rounded-circle d-inline-flex align-items-center justify-content-center shadow mb-3"
                style={{
                  width: "72px",
                  height: "72px",
                }}
              >
                <i className="fs-3 fa-solid fa-cube"></i>
              </div>

              <h2 className="fw-bold mb-2">Track Your Order</h2>

              <p className="mb-0 opacity-75">
                Enter your order or tracking number and phone number to check
                your delivery status.
              </p>
            </div>

            {/* Card Body */}
            <div className="card-body p-4 p-md-5">
              <form onSubmit={handleSubmit} noValidate>
                {/* Tracking Number */}
                <div className="mb-4">
                  <label
                    htmlFor="trackingId"
                    className="form-label fw-semibold"
                  >
                    Order / Tracking Number{" "}
                    <span className="text-danger">*</span>
                  </label>

                  <div className="input-group tp-contact-4__input-box">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="fa-brands fa-sistrix me-1"></i>
                    </span>

                    <input
                      id="trackingId"
                      name="trackingId"
                      type="text"
                      autoFocus
                      placeholder="e.g. ORD123456 or TRK987654"
                      value={values.trackingId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="form-control border-start-0"
                    />
                  </div>

                  {/* Tracking Error */}
                  {touched.trackingId && errors.trackingId && (
                    <div className="error mt-1">{errors.trackingId}</div>
                  )}
                </div>

                {/* Phone Number */}
                <div className="col-md-12 height-4 mb-30">
                  <label
                    htmlFor="mobile"
                    className="form-label fw-semibold"
                  >
                    Mobile Number{" "}
                    <span className="text-danger">*</span>
                  </label>

                  <PhoneInput
                    country="us"
                    value={values.mobile}
                    onChange={(phone) => {
                      setFieldValue(
                        "mobile",
                        phone ? `+${phone}` : "",
                      );
                    }}
                    onBlur={() => {
                      setFieldTouched("mobile", true);
                    }}
                    disabled={isSubmitting}
                    inputProps={{
                      id: "mobile",
                      name: "mobile",
                      required: true,
                      autoComplete: "tel",
                    }}
                    enableSearch
                    disableSearchIcon
                    countryCodeEditable={false}
                     
                  />

                  {/* Mobile Error */}
                  {touched.mobile && errors.mobile && (
                    <div className="error mt-1">{errors.mobile}</div>
                  )}
                </div>

                {/* API Error */}
                {status && (
                  <div
                    className="alert alert-danger d-flex align-items-start gap-2"
                    role="alert"
                  >
                    <i className="bi bi-exclamation-circle-fill mt-1"></i>

                    <div>{status}</div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100 fw-semibold shadow-sm"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />

                      Tracking Order...
                    </>
                  ) : (
                    <>
                      Track Order
                      <i className="bi bi-arrow-right ms-2"></i>
                    </>
                  )}
                </button>
              </form>

              {/* Help Section */}
              <div className="border-top mt-4 pt-4">
                <h6 className="fw-semibold text-muted mb-3"> 
                  Where can I find my order/tracking number?
                </h6>

                <ul className="list-unstyled mb-0">
                  <li className="d-flex align-items-start mb-2 text-muted small">
                    <i class="fa-regular fa-circle-check me-2 mt-1"></i> 

                    <span>
                      In your order confirmation email.
                    </span>
                  </li>

                  <li className="d-flex align-items-start mb-2 text-muted small">
                   <i class="fa-regular fa-circle-check me-2 mt-1"></i> 

                    <span>
                      In the Orders section of your account.
                    </span>
                  </li>

                  <li className="d-flex align-items-start text-muted small">
                   <i class="fa-regular fa-circle-check me-2 mt-1"></i> 

                    <span>
                      In your shipping confirmation email.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Contact Support */}
          <div className="text-center mt-4">
            <p className="text-muted small mb-0">
              Still can't find your tracking number?{" "}
              <Link
                to="/contact"
                className="fw-semibold text-primary text-decoration-none"
              >
                Contact Support
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
